// src/views/SimulatorView.js
import React, { useState, useMemo } from 'react';
import { ComposedChart, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Calculator, RotateCcw, Loader2, Bot, Activity, Trash2 } from 'lucide-react';
import { API_BASE } from '../api/db';

export default function SimulatorView({ simTicker, setSimTicker, themeClasses }) {
  const [simStrategy, setSimStrategy] = useState("point"); 
  
  const [simDate, setSimDate] = useState("");
  const [simEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [simShares, setSimShares] = useState(1);
  const [simAmount, setSimAmount] = useState(500); 
  
  // Compound Interest / Bond Specific State
  const [simMonthly, setSimMonthly] = useState(500);
  const [simRate, setSimRate] = useState(7);
  const [simYears, setSimYears] = useState(20);

  const [simPortfolio, setSimPortfolio] = useState([]); 
  const [simData, setSimData] = useState(null); 
  const [isSimLoading, setIsSimLoading] = useState(false);
  const [simError, setSimError] = useState("");

  const dateLimits = useMemo(() => {
    const today = new Date();
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(today.getFullYear() - 5);
    return { min: fiveYearsAgo.toISOString().split('T')[0], max: today.toISOString().split('T')[0] };
  }, []);

  const setDatePreset = (monthsBack) => {
    const d = new Date();
    d.setMonth(d.getMonth() - monthsBack);
    setSimDate(d.toISOString().split('T')[0]);
  };

  const handleReset = () => {
    setSimTicker("");
    setSimDate("");
    setSimShares(1);
    setSimAmount(500);
    setSimMonthly(500);
    setSimRate(7);
    setSimYears(20);
    setSimPortfolio([]);
    setSimData(null);
    setSimError("");
  };

  const runSimulation = async () => {
    // Skip ticker/date validation if we are just calculating raw interest
    if (simStrategy !== "interest") {
      if(!simTicker) {
        setSimError("Please enter a ticker symbol.");
        return;
      }
      if (simStrategy !== "bond_etf" && !simDate) {
        setSimError("Please select a start date.");
        return;
      }
    }
    
    setIsSimLoading(true);
    setSimError("");
    setSimData(null);

    // --- LOCAL EXECUTION FOR COMPOUND INTEREST ---
    if (simStrategy === "interest") {
      let balance = Number(simAmount);
      const monthlyRate = Number(simRate) / 100 / 12;
      let invested = Number(simAmount);
      const chartData = [];

      for (let y = 1; y <= Number(simYears); y++) {
        for (let m = 1; m <= 12; m++) {
          balance = balance * (1 + monthlyRate) + Number(simMonthly);
          invested += Number(simMonthly);
        }
        chartData.push({
          date: `Year ${y}`,
          invested: Math.round(invested),
          interest: Math.round(balance - invested),
          balance: Math.round(balance)
        });
      }

      setSimData({
        type: "interest",
        data: chartData,
        total_invested: invested,
        total_interest: balance - invested,
        final_value: balance
      });
      setIsSimLoading(false);
      return;
    }

    const t = simTicker.toUpperCase().trim();

    // --- EXECUTION FOR BOND ETF YIELD FETCHING ---
    if (simStrategy === "bond_etf") {
      try {
        const res = await fetch(`${API_BASE}/company/${t}`);
        const profile = await res.json();
        
        let rawYield = profile.secYield || profile.divYield || "0";
        if (rawYield === "N/A" || rawYield === "Check Prospectus") rawYield = "0";
        const fetchedRate = parseFloat(rawYield.replace('%', ''));

        if (fetchedRate <= 0) {
          throw new Error(`Could not detect a valid yield for ${t}. Ensure this is a dividend or yield-paying asset.`);
        }

        let balance = Number(simAmount);
        const monthlyRate = fetchedRate / 100 / 12;
        let invested = Number(simAmount);
        const chartData = [];

        for (let y = 1; y <= Number(simYears); y++) {
          for (let m = 1; m <= 12; m++) {
            balance = balance * (1 + monthlyRate) + Number(simMonthly);
            invested += Number(simMonthly);
          }
          chartData.push({ date: `Year ${y}`, invested: Math.round(invested), interest: Math.round(balance - invested), balance: Math.round(balance) });
        }

        setSimData({ type: "bond_etf", ticker: t, rate: fetchedRate, data: chartData, total_invested: invested, total_interest: balance - invested, final_value: balance });
      } catch (e) {
        setSimError(e.message);
      } finally {
        setIsSimLoading(false);
      }
      return;
    }

    // --- BACKEND API EXECUTION FOR MARKET MODELS ---
    try {
      if (simStrategy === "point") {
        if (simShares <= 0) throw new Error("Invalid share amount.");
        
        const flatFileRes = await fetch(`${API_BASE}/model/flatfile`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticker: t, date: simDate })
        });
        
        const fileData = await flatFileRes.json();
        if (!flatFileRes.ok) throw new Error(fileData.error || "Failed to process flat file.");
        
        const firstTrade = fileData.sample_records[0];
        const histPrice = firstTrade.close || firstTrade.price || firstTrade.c || firstTrade.p;
        if (!histPrice) throw new Error("Could not parse price from Flat File data.");

        // Swapped direct API call for the secure backend proxy
        const prevRes = await fetch(`${API_BASE}/massive/v2/aggs/ticker/${t}/prev?adjusted=true`);
        const prevData = await prevRes.json();
        const currentPrice = prevData.results?.[0]?.c || histPrice;

        const costBasis = histPrice * simShares;
        const currentVal = currentPrice * simShares;
        const pl = currentVal - costBasis;
        const plPercent = ((currentPrice - histPrice) / histPrice) * 100;

        setSimPortfolio(prev => [...prev, { ticker: t, date: simDate, shares: Number(simShares), buyPrice: histPrice, currentPrice, costBasis, pl, plPercent }]);
        
      } else {
        const res = await fetch(`${API_BASE}/model/simulate`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticker: t, strategy: simStrategy, startDate: simDate, endDate: simEndDate, amount: simAmount })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setSimData({ ticker: t, ...data });
      }
    } catch (e) { 
      setSimError(e.message); 
    } finally { 
      setIsSimLoading(false); 
    }
  };

  const simDescriptions = {
    point: "Single Trade Engine: Backtest exactly what a single, lump-sum investment executed on a historical day would be worth today.",
    dca: "Dollar Cost Averaging: Simulates investing a fixed dollar amount every single month. This standard strategy mathematically reduces volatility by lowering your average cost basis during crashes.",
    pit_vs_dca: "Lump Sum vs DCA: Divides a fixed Total Budget across all months to compare Dollar-Cost Averaging directly against making a single Point-In-Time lump sum trade on the start date.",
    drip: "Dividend Snowball (DRIP): Simulates a lump sum investment where every cash dividend paid is automatically reinvested to buy fractional shares, compounding yield over time.",
    sma: "Trend-Following Bot: Algorithmic momentum execution. Buys the asset when its 50-day average crosses above its 200-day average (Golden Cross), and dumps the asset when it drops below (Death Cross).",
    rsi: "Mean-Reversion Bot: Algorithmic dip-buying. Executes a buy order when the asset crashes and triggers an 'Oversold' signal (RSI < 30), and executes a sell order when it gets 'Overbought' (RSI > 70).",
    monte_carlo: "Probability Matrix: Projects 20 randomized futures over the next 12 months based on the asset's historical mean returns and mathematical standard deviation (volatility).",
    interest: "Compound Interest: Model the exponential growth of your cash investments over time through regular contributions and a fixed annual rate of return.",
    bond_etf: "Fixed Income Projector: Automatically fetches the live yield of a Bond ETF and maps out your total cash flow compounding over years, assuming price stability."
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
       <div>
         <h1 className={`text-3xl font-black ${themeClasses.textHead} mb-2 flex items-center gap-3`}><Calculator className="text-indigo-500" size={32}/> Quantitative Simulator</h1>
         <p className={`text-sm ${themeClasses.textMuted} font-medium`}>Backtest historical scenarios, test algorithmic execution bots, and project future probabilities.</p>
       </div>

       <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl overflow-hidden flex flex-col`}>
          <div className={`p-4 border-b ${themeClasses.border} ${themeClasses.input} flex justify-between items-center shrink-0`}>
            <div className="flex gap-2 overflow-x-auto custom-scrollbar pr-4">
              {["point", "dca", "pit_vs_dca", "drip", "sma", "rsi", "monte_carlo", "bond_etf", "interest"].map(strat => (
                <button 
                  key={strat} 
                  onClick={() => { setSimStrategy(strat); setSimData(null); setSimError(""); }}
                  className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors shrink-0 ${simStrategy === strat ? 'bg-indigo-600 text-white' : `${themeClasses.textMuted} hover:text-indigo-500`}`}
                >
                  {strat === 'point' ? 'PIT' : strat === 'dca' ? 'DCA' : strat === 'pit_vs_dca' ? 'PIT VS DCA' : strat === 'drip' ? 'DRIP' : strat === 'sma' ? 'SMA' : strat === 'rsi' ? 'RSI' : strat === 'monte_carlo' ? 'Monte Carlo' : strat === 'bond_etf' ? 'Bond ETF' : 'Interest'}
                </button>
              ))}
            </div>
            <button onClick={handleReset} className={`ml-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors shrink-0 ${themeClasses.input} border ${themeClasses.border} hover:border-red-500 hover:text-red-500 flex items-center gap-2`}>
              <RotateCcw size={12}/> Reset
            </button>
          </div>

          <div className={`p-8 border-b ${themeClasses.border} flex flex-wrap gap-6 items-start shrink-0`}>
            
            {/* Ticker is hidden if using the generic Interest Calculator */}
            {simStrategy !== "interest" && (
              <div className="flex-1 min-w-[120px]">
                 <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Ticker Symbol</label>
                 <input type="text" value={simTicker} onChange={(e) => setSimTicker(e.target.value)} placeholder={simStrategy === 'bond_etf' ? "e.g. VTIP" : "e.g. AAPL"} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
              </div>
            )}

            {simStrategy === "point" && (
              <>
                <div className="flex-1 min-w-[150px]">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Historical Buy Date</label>
                   <input type="date" value={simDate} min={dateLimits.min} max={dateLimits.max} onChange={(e) => setSimDate(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                   <div className="flex gap-2 overflow-x-auto custom-scrollbar mt-2">
                     {[{label: '5Y', m: 60}, {label: '4Y', m: 48}, {label: '3Y', m: 36}, {label: '2Y', m: 24}, {label: '1Y', m: 12}, {label: '6M', m: 6}, {label: '3M', m: 3}].map(btn => (
                       <button type="button" key={btn.label} onClick={() => setDatePreset(btn.m)} className={`px-2 py-1 text-[9px] font-black text-indigo-500 bg-indigo-500/10 uppercase rounded transition-colors hover:bg-indigo-500/20`}>
                         {btn.label}
                       </button>
                     ))}
                   </div>
                </div>
                <div className="w-32">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Shares</label>
                   <input type="number" value={simShares} onChange={(e) => setSimShares(e.target.value)} min="1" className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold text-center`} />
                </div>
              </>
            )}

            {(simStrategy === "dca" || simStrategy === "sma" || simStrategy === "rsi" || simStrategy === "pit_vs_dca" || simStrategy === "drip") && (
              <>
                <div className="w-32">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>
                     {simStrategy === 'dca' ? 'Monthly ($)' : simStrategy === 'pit_vs_dca' ? 'Total Budget ($)' : 'Initial ($)'}
                   </label>
                   <input type="number" value={simAmount} onChange={(e) => setSimAmount(e.target.value)} min="1" className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold text-center`} />
                </div>
                <div className="flex-1 min-w-[150px]">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Start Date</label>
                   <input type="date" value={simDate} onChange={(e) => setSimDate(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                   <div className="flex gap-2 overflow-x-auto custom-scrollbar mt-2">
                     {[{label: '5Y', m: 60}, {label: '4Y', m: 48}, {label: '3Y', m: 36}, {label: '2Y', m: 24}, {label: '1Y', m: 12}, {label: '6M', m: 6}, {label: '3M', m: 3}].map(btn => (
                       <button type="button" key={btn.label} onClick={() => setDatePreset(btn.m)} className={`px-2 py-1 text-[9px] font-black text-indigo-500 bg-indigo-500/10 uppercase rounded transition-colors hover:bg-indigo-500/20`}>
                         {btn.label}
                       </button>
                     ))}
                   </div>
                </div>
              </>
            )}

            {simStrategy === "monte_carlo" && (
               <div className="flex-1 min-w-[150px]">
                  <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Sample Since (Volatility)</label>
                  <input type="date" value={simDate} onChange={(e) => setSimDate(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                  <div className="flex gap-2 overflow-x-auto custom-scrollbar mt-2">
                     {[{label: '5Y', m: 60}, {label: '4Y', m: 48}, {label: '3Y', m: 36}, {label: '2Y', m: 24}, {label: '1Y', m: 12}, {label: '6M', m: 6}, {label: '3M', m: 3}].map(btn => (
                       <button type="button" key={btn.label} onClick={() => setDatePreset(btn.m)} className={`px-2 py-1 text-[9px] font-black text-indigo-500 bg-indigo-500/10 uppercase rounded transition-colors hover:bg-indigo-500/20`}>
                         {btn.label}
                       </button>
                     ))}
                   </div>
               </div>
            )}

            {(simStrategy === "interest" || simStrategy === "bond_etf") && (
              <>
                <div className="flex-1 min-w-[150px]">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Initial Principal ($)</label>
                   <input type="number" value={simAmount} onChange={(e) => setSimAmount(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                </div>
                <div className="flex-1 min-w-[150px]">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Monthly Addition ($)</label>
                   <input type="number" value={simMonthly} onChange={(e) => setSimMonthly(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                </div>
                {simStrategy === "interest" && (
                <div className="flex-1 min-w-[150px]">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Annual Return (%)</label>
                   <input type="number" value={simRate} onChange={(e) => setSimRate(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                </div>
                )}
                <div className="w-32">
                   <label className={`text-[10px] font-bold ${themeClasses.textMuted} uppercase block mb-2`}>Years to Grow</label>
                   <input type="number" value={simYears} onChange={(e) => setSimYears(e.target.value)} className={`w-full ${themeClasses.bgCard} p-4 rounded-xl ${themeClasses.textMain} border ${themeClasses.border} outline-none focus:border-indigo-500 text-sm font-bold`} />
                </div>
              </>
            )}

            {/* Hidden label aligns the execute button height perfectly with the text boxes */}
            <div>
              <label className="text-[10px] opacity-0 block mb-2">Execute</label>
              <button onClick={runSimulation} disabled={isSimLoading} className="px-8 py-4 bg-indigo-600 rounded-xl text-white font-black uppercase tracking-widest text-xs hover:bg-indigo-500 transition-colors h-[54px] flex items-center justify-center min-w-[180px] shadow-lg shadow-indigo-500/20">
                {isSimLoading ? <Loader2 size={16} className="animate-spin"/> : "Execute"}
              </button>
            </div>
          </div>

          <div className="flex-1 p-8 min-h-[400px]">
             {simError && (
               <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-4 rounded-xl text-xs font-bold uppercase tracking-widest mb-6">
                 Error: {simError}
               </div>
             )}

             {/* BLANK STATE EXPLANATIONS */}
             {!simData && simStrategy !== "point" && (
               <div className={`flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center ${themeClasses.textMuted}`}>
                 <Bot size={48} className="mb-6 opacity-50" />
                 <h3 className="font-black uppercase tracking-widest text-indigo-500 mb-2">Algorithm Ready</h3>
                 <p className="text-sm leading-relaxed">{simDescriptions[simStrategy]}</p>
               </div>
             )}

             {/* RENDER 1: Point in Time */}
             {simStrategy === "point" && (
               simPortfolio.length === 0 ? (
                 <div className={`flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center ${themeClasses.textMuted}`}>
                   <Activity size={48} className="mb-6 opacity-50" />
                   <h3 className="font-black uppercase tracking-widest text-indigo-500 mb-2">Algorithm Ready</h3>
                   <p className="text-sm leading-relaxed">{simDescriptions[simStrategy]}</p>
                 </div>
               ) : (
                 <table className="w-full text-left text-sm">
                    <thead>
                      <tr className={`${themeClasses.textMuted} uppercase text-[10px] tracking-widest border-b ${themeClasses.border}`}>
                        <th className="pb-4 font-black">Asset</th>
                        <th className="pb-4 font-black">Entry Date</th>
                        <th className="pb-4 font-black text-right">Shares</th>
                        <th className="pb-4 font-black text-right">Cost Basis</th>
                        <th className="pb-4 font-black text-right">Current Value</th>
                        <th className="pb-4 font-black text-right">Total P/L</th>
                        <th className="pb-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {simPortfolio.map((sim, i) => (
                        <tr key={i} className={`border-b ${themeClasses.border} hover:bg-slate-500/5 transition-colors`}>
                          <td className={`py-5 font-black ${themeClasses.textHead}`}>{sim.ticker}</td>
                          <td className={`py-5 ${themeClasses.textMuted} font-mono text-xs`}>{sim.date}</td>
                          <td className={`py-5 ${themeClasses.textMain} text-right font-bold`}>{sim.shares}</td>
                          <td className={`py-5 text-right font-mono ${themeClasses.textMuted}`}>${sim.costBasis.toFixed(2)} <span className="block text-[9px] opacity-60">@ ${sim.buyPrice.toFixed(2)}/sh</span></td>
                          <td className="py-5 text-right font-mono text-emerald-500 font-medium">${sim.currentPrice.toFixed(2)}</td>
                          <td className={`py-5 text-right font-mono font-black ${sim.pl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                             {sim.pl >= 0 ? '+' : ''}${sim.pl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} 
                             <span className="block text-[10px] opacity-80 mt-0.5">{sim.plPercent > 0 ? '+' : ''}{sim.plPercent.toFixed(2)}%</span>
                          </td>
                          <td className="py-5 text-right"><button onClick={() => setSimPortfolio(simPortfolio.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={16}/></button></td>
                        </tr>
                      ))}
                    </tbody>
                 </table>
               )
             )}

             {/* RENDER 2: Dollar Cost Averaging */}
             {simStrategy === "dca" && simData && (
               <div className="flex flex-col gap-6">
                 <div className="flex justify-between items-center bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Invested</span>
                     <span className={`text-2xl font-mono font-bold ${themeClasses.textHead}`}>${simData.total_invested.toLocaleString()}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Final Portfolio Value</span>
                     <span className={`text-2xl font-mono font-bold ${simData.final_value >= simData.total_invested ? 'text-emerald-500' : 'text-red-500'}`}>
                       ${simData.final_value.toLocaleString(undefined, {maximumFractionDigits: 2})}
                     </span>
                   </div>
                 </div>
                 <div className="h-[350px] w-full shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={30} axisLine={false} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={10} tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} width={60} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}}/>
                        <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}/>
                        <Area type="monotone" dataKey="total_invested" name="Total Invested ($)" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
                        <Line type="monotone" dataKey="portfolio_value" name="Portfolio Value ($)" stroke="#10b981" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                 </div>
                 <div className="mt-4">
                    <h3 className={`text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4`}>Monthly Execution Ledger</h3>
                    <div className="h-64 overflow-y-auto custom-scrollbar border border-indigo-500/20 rounded-xl bg-indigo-500/5">
                      <table className="w-full text-left text-xs">
                          <thead className="sticky top-0 bg-slate-900 border-b border-indigo-500/20 text-indigo-400">
                            <tr>
                              <th className="p-3 font-bold uppercase tracking-widest">Date</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Action</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Price</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Shares Acq.</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Total Shares</th>
                            </tr>
                          </thead>
                          <tbody>
                            {simData.ledger.map((trade, i) => (
                              <tr key={i} className="border-b border-indigo-500/10 hover:bg-indigo-500/10 transition-colors">
                                <td className={`p-3 font-mono ${themeClasses.textHead}`}>{trade.date}</td>
                                <td className={`p-3 text-right font-black text-indigo-400`}>BUY ${simAmount}</td>
                                <td className={`p-3 text-right font-mono ${themeClasses.textMuted}`}>${trade.close.toFixed(2)}</td>
                                <td className={`p-3 text-right font-mono text-emerald-500`}>+{trade.shares_bought.toFixed(4)}</td>
                                <td className={`p-3 text-right font-mono font-bold ${themeClasses.textHead}`}>{trade.total_shares.toFixed(4)}</td>
                              </tr>
                            ))}
                          </tbody>
                      </table>
                    </div>
                 </div>
               </div>
             )}

             {/* RENDER 3: PiT vs DCA */}
             {simStrategy === "pit_vs_dca" && simData && (
               <div className="flex flex-col gap-6">
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Budget</span>
                     <span className={`text-xl font-mono font-bold ${themeClasses.textHead}`}>${simData.total_budget.toLocaleString()}</span>
                   </div>
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">DCA Monthly ($)</span>
                     <span className={`text-xl font-mono font-bold ${themeClasses.textHead}`}>${simData.monthly_amount.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Lump Sum (PIT) Value</span>
                     <span className={`text-xl font-mono font-bold ${simData.pit_final >= simData.total_budget ? 'text-emerald-500' : 'text-red-500'}`}>
                       ${simData.pit_final.toLocaleString(undefined, {maximumFractionDigits: 2})}
                     </span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">DCA Final Value</span>
                     <span className={`text-xl font-mono font-bold ${simData.dca_final >= simData.total_budget ? 'text-emerald-500' : 'text-red-500'}`}>
                       ${simData.dca_final.toLocaleString(undefined, {maximumFractionDigits: 2})}
                     </span>
                   </div>
                 </div>
                 
                 <div className="h-[350px] w-full shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={30} axisLine={false} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={10} tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} width={60} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}}/>
                        <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}/>
                        
                        <Line type="monotone" dataKey="invested_pit" name="Lump Sum Invested" stroke="#64748b" strokeWidth={1} strokeDasharray="3 3" dot={false} />
                        <Area type="monotone" dataKey="invested_dca" name="DCA Invested" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
                        
                        <Line type="monotone" dataKey="value_pit" name="PIT Value" stroke="#ec4899" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="value_dca" name="DCA Value" stroke="#10b981" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                 </div>
                 
                 <div className="mt-4">
                    <h3 className={`text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2`}>Execution Ledger</h3>
                    <p className={`text-[10px] ${themeClasses.textMuted} mb-4`}>*Lump sum purchased {simData.shares_pit.toFixed(2)} shares at ${simData.initial_price.toFixed(2)} on {simData.data[0].date}.</p>
                    <div className="h-64 overflow-y-auto custom-scrollbar border border-indigo-500/20 rounded-xl bg-indigo-500/5">
                      <table className="w-full text-left text-xs">
                          <thead className="sticky top-0 bg-slate-900 border-b border-indigo-500/20 text-indigo-400">
                            <tr>
                              <th className="p-3 font-bold uppercase tracking-widest">Date</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Action</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Price</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Shares Acq.</th>
                              <th className="p-3 font-bold uppercase tracking-widest text-right">Total DCA Shares</th>
                            </tr>
                          </thead>
                          <tbody>
                            {simData.ledger.map((trade, i) => (
                              <tr key={i} className="border-b border-indigo-500/10 hover:bg-indigo-500/10 transition-colors">
                                <td className={`p-3 font-mono ${themeClasses.textHead}`}>{trade.date}</td>
                                <td className={`p-3 text-right font-black text-indigo-400`}>{trade.dca_action}</td>
                                <td className={`p-3 text-right font-mono ${themeClasses.textMuted}`}>${trade.close.toFixed(2)}</td>
                                <td className={`p-3 text-right font-mono text-emerald-500`}>+{trade.dca_shares.toFixed(4)}</td>
                                <td className={`p-3 text-right font-mono font-bold ${themeClasses.textHead}`}>{trade.dca_total_shares.toFixed(4)}</td>
                              </tr>
                            ))}
                          </tbody>
                      </table>
                    </div>
                 </div>
               </div>
             )}

             {/* RENDER 4: DRIP SIMULATOR */}
             {simStrategy === "drip" && simData && (
               <div className="flex flex-col gap-6">
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Cash Invested</span>
                     <span className={`text-xl font-mono font-bold ${themeClasses.textHead}`}>${simAmount.toLocaleString()}</span>
                   </div>
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Cash Dividends</span>
                     <span className={`text-xl font-mono font-bold text-emerald-400`}>+${simData.total_dividends.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Final Value (No DRIP)</span>
                     <span className={`text-xl font-mono font-bold ${simData.final_no_drip >= simAmount ? 'text-slate-300' : 'text-red-400'}`}>
                       ${simData.final_no_drip.toLocaleString(undefined, {maximumFractionDigits: 2})}
                     </span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Final Value (With DRIP)</span>
                     <span className={`text-xl font-mono font-bold ${simData.final_value >= simAmount ? 'text-emerald-500' : 'text-red-500'}`}>
                       ${simData.final_value.toLocaleString(undefined, {maximumFractionDigits: 2})}
                     </span>
                   </div>
                 </div>
                 <div className="h-[350px] w-full shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={30} axisLine={false} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={10} tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} width={60} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}}/>
                        <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}/>
                        
                        <Line type="monotone" dataKey="value_no_drip" name="No DRIP (Price Action Only)" stroke="#64748b" strokeWidth={1} strokeDasharray="3 3" dot={false} />
                        <Line type="monotone" dataKey="value_drip" name="With DRIP (Compound Value)" stroke="#10b981" strokeWidth={3} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                 </div>
                 <div className="mt-4">
                    <h3 className={`text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2`}>Dividend Reinvestment Ledger</h3>
                    <p className={`text-[10px] ${themeClasses.textMuted} mb-4`}>*Initial purchase of {simData.initial_shares.toFixed(2)} shares. Final holding of {simData.final_shares.toFixed(2)} shares.</p>
                    
                    {simData.ledger.length === 0 ? (
                       <div className={`text-center py-10 ${themeClasses.textMuted} text-xs uppercase tracking-widest font-black`}>This company paid zero dividends during this period.</div>
                    ) : (
                      <div className="h-64 overflow-y-auto custom-scrollbar border border-indigo-500/20 rounded-xl bg-indigo-500/5">
                        <table className="w-full text-left text-xs">
                            <thead className="sticky top-0 bg-slate-900 border-b border-indigo-500/20 text-indigo-400">
                              <tr>
                                <th className="p-3 font-bold uppercase tracking-widest">Ex-Div Date</th>
                                <th className="p-3 font-bold uppercase tracking-widest text-right">Cash Received</th>
                                <th className="p-3 font-bold uppercase tracking-widest text-right">Reinvest Price</th>
                                <th className="p-3 font-bold uppercase tracking-widest text-right">Shares Added</th>
                                <th className="p-3 font-bold uppercase tracking-widest text-right">New Total Shares</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simData.ledger.map((trade, i) => (
                                <tr key={i} className="border-b border-indigo-500/10 hover:bg-indigo-500/10 transition-colors">
                                  <td className={`p-3 font-mono ${themeClasses.textHead}`}>{trade.date}</td>
                                  <td className={`p-3 text-right font-mono text-emerald-400`}>+${trade.payout_total.toFixed(2)}</td>
                                  <td className={`p-3 text-right font-mono ${themeClasses.textMuted}`}>${trade.close.toFixed(2)}</td>
                                  <td className={`p-3 text-right font-mono text-emerald-500`}>+{trade.dca_shares.toFixed(4)}</td>
                                  <td className={`p-3 text-right font-mono font-bold ${themeClasses.textHead}`}>{trade.dca_total_shares.toFixed(4)}</td>
                                </tr>
                              ))}
                            </tbody>
                        </table>
                      </div>
                    )}
                 </div>
               </div>
             )}

             {/* RENDER 5 & 6: ALGO BOTS (SMA & RSI) */}
             {(simStrategy === "sma" || simStrategy === "rsi") && simData && (
               <div className="flex flex-col gap-6">
                 <div className="flex justify-between items-center bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                   <div>
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Algo Bot Final Value</span>
                     <span className={`text-2xl font-mono font-bold ${simData.final_value >= simAmount ? 'text-emerald-500' : 'text-red-500'}`}>${simData.final_value.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Buy & Hold Final Value</span>
                     <span className={`text-2xl font-mono font-bold ${simData.buy_hold >= simAmount ? 'text-slate-300' : 'text-red-400'}`}>${simData.buy_hold.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                   </div>
                 </div>
                 <div className="h-[350px] w-full shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={30} axisLine={false} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={10} tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} width={60} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}}/>
                        <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}/>
                        <Line type="monotone" dataKey="buy_hold" name="Buy & Hold" stroke="#ef4444" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="equity_curve" name="Bot Execution" stroke="#10b981" strokeWidth={3} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                 </div>
                 <div className="mt-4">
                    <h3 className={`text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4`}>Algorithmic Execution Log</h3>
                    {simData.ledger.length === 0 ? (
                       <div className={`text-center py-10 ${themeClasses.textMuted} text-xs uppercase tracking-widest font-black`}>Bot triggered zero executions during this period.</div>
                    ) : (
                      <div className="h-64 overflow-y-auto custom-scrollbar border border-indigo-500/20 rounded-xl bg-indigo-500/5">
                        <table className="w-full text-left text-xs">
                            <thead className="sticky top-0 bg-slate-900 border-b border-indigo-500/20 text-indigo-400">
                              <tr>
                                <th className="p-3 font-bold uppercase tracking-widest">Execution Date</th>
                                <th className="p-3 font-bold uppercase tracking-widest">Signal</th>
                                {simStrategy === 'rsi' && <th className="p-3 font-bold uppercase tracking-widest text-right">RSI Value</th>}
                                <th className="p-3 font-bold uppercase tracking-widest text-right">Execution Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simData.ledger.map((trade, i) => (
                                <tr key={i} className="border-b border-indigo-500/10 hover:bg-indigo-500/10 transition-colors">
                                  <td className={`p-3 font-mono ${themeClasses.textHead}`}>{trade.date}</td>
                                  <td className={`p-3 font-black ${trade.type.includes('BUY') ? 'text-emerald-500' : 'text-red-500'}`}>{trade.type}</td>
                                  {simStrategy === 'rsi' && <td className={`p-3 text-right font-mono ${themeClasses.textMuted}`}>{trade.rsi}</td>}
                                  <td className={`p-3 text-right font-mono ${themeClasses.textMuted}`}>${trade.price.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                        </table>
                      </div>
                    )}
                 </div>
               </div>
             )}

             {/* RENDER 7: Monte Carlo */}
             {simStrategy === "monte_carlo" && simData && (
               <div className="flex flex-col gap-6">
                 <div className="grid grid-cols-4 gap-4">
                   <div className="bg-indigo-500/5 p-4 rounded-xl border border-indigo-500/20">
                     <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest block">Current Price</span>
                     <span className={`text-lg font-mono font-bold ${themeClasses.textHead}`}>${simData.stats.current.toFixed(2)}</span>
                   </div>
                   <div className="bg-indigo-500/5 p-4 rounded-xl border border-indigo-500/20">
                     <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest block">Projected Mean</span>
                     <span className={`text-lg font-mono font-bold text-blue-400`}>${simData.stats.mean.toFixed(2)}</span>
                   </div>
                   <div className="bg-red-500/5 p-4 rounded-xl border border-red-500/20">
                     <span className="text-[9px] font-black text-red-500 uppercase tracking-widest block">Worst Case (10th Pct)</span>
                     <span className={`text-lg font-mono font-bold text-red-500`}>${simData.stats.worst.toFixed(2)}</span>
                   </div>
                   <div className="bg-emerald-500/5 p-4 rounded-xl border border-emerald-500/20">
                     <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest block">Best Case (90th Pct)</span>
                     <span className={`text-lg font-mono font-bold text-emerald-500`}>${simData.stats.best.toFixed(2)}</span>
                   </div>
                 </div>
                 <div className="h-[350px] w-full shrink-0 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis 
                          dataKey="day" 
                          stroke="#475569" 
                          fontSize={10} 
                          minTickGap={30} 
                          axisLine={false} 
                          tickLine={false} 
                          label={{ value: 'Future Trading Days (252 = 1 Year)', position: 'insideBottomRight', offset: -10, fill: '#64748b', fontSize: 10 }} 
                        />
                        <YAxis domain={['auto', 'auto']} stroke="#475569" fontSize={10} tickFormatter={v => `$${v}`} axisLine={false} tickLine={false} width={60} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}}/>
                        
                        {/* The 3 Percentile Paths */}
                        <Line type="monotone" dataKey="best" name="Best Case (90%)" stroke="#10b981" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                        <Line type="monotone" dataKey="median" name="Expected (50%)" stroke="#6366f1" strokeWidth={3} dot={false} />
                        <Line type="monotone" dataKey="worst" name="Worst Case (10%)" stroke="#ef4444" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="absolute top-2 left-16 bg-black/50 px-2 py-1 rounded text-[9px] text-slate-400 font-bold uppercase tracking-widest border border-slate-800 backdrop-blur-sm">
                      1,000 Iterations (10th, 50th, 90th Percentiles)
                    </div>
                 </div>
               </div>
             )}

             {/* RENDER 8 & 9: BOND ETF and COMPOUND INTEREST */}
             {(simStrategy === "interest" || simStrategy === "bond_etf") && simData && (
               <div className="flex flex-col gap-6">
                 
                 {simStrategy === "bond_etf" && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-xl text-emerald-500 text-xs font-black uppercase tracking-widest text-center">
                        Successfully Fetched <span className="text-white bg-emerald-500 px-2 py-0.5 rounded mx-1">{simData.ticker}</span> Yield: {simData.rate}%
                    </div>
                 )}

                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                   <div className="bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-1">Total Principal</span>
                     <span className={`text-2xl font-mono font-bold ${themeClasses.textHead}`}>${simData.total_invested.toLocaleString()}</span>
                   </div>
                   <div className="bg-indigo-500/5 p-6 rounded-2xl border border-indigo-500/20">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-1">Total Yield Earned</span>
                     <span className={`text-2xl font-mono font-bold text-indigo-400`}>+${simData.total_interest.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                   </div>
                   <div className="bg-indigo-500/10 p-6 rounded-2xl border border-indigo-500/30">
                     <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-1">Final Future Value</span>
                     <span className={`text-2xl font-mono font-black text-indigo-500`}>${simData.final_value.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                   </div>
                 </div>

                 <div className="h-[350px] w-full shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={simData.data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} minTickGap={30} axisLine={false} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={10} tickFormatter={v => `$${v.toLocaleString()}`} axisLine={false} tickLine={false} width={80} />
                        <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}} formatter={(val) => `$${val.toLocaleString()}`}/>
                        <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}/>
                        <Area type="monotone" dataKey="invested" name="Principal Contributed" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="interest" name={simStrategy === 'bond_etf' ? "Yield Compounded" : "Interest Compounded"} stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                 </div>
               </div>
             )}
          </div>
       </div>
    </div>
  );
}